import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export const RATING_LABELS = {
  intensity: ['Very easy', 'Easy', 'Moderate', 'Hard', 'Max effort'],
  recovery: ['Very sore', 'Sore', 'Okay', 'Good', 'Fully recovered'],
};

/**
 * Daily check-in: three plain choices instead of an ambiguous 1–5. Stored as 2 / 3 / 5 on the
 * recovery_checkins 1–5 scale, which the bot's digest and the iOS app read (≤2 means "go lighter").
 */
export const CHECKIN_CHOICES = [
  { rating: 2, title: 'Sore', short: 'Sore', detail: 'Still feeling my last session', color: 'var(--danger)' },
  { rating: 3, title: 'Okay', short: 'Okay', detail: 'A bit stiff, good to go', color: 'var(--warning)' },
  { rating: 5, title: 'Ready to train', short: 'Ready', detail: 'Fresh, no soreness', color: 'var(--accent)' },
];

/** The check-in choice a stored rating belongs to (older 1–5 check-ins included). */
export const checkinChoice = (rating: number) => CHECKIN_CHOICES[rating <= 2 ? 0 : rating <= 4 ? 1 : 2];

const RATING_COLORS = ['var(--danger)', '#ff8f5c', 'var(--warning)', '#9edb7a', 'var(--accent)'];
export const ratingColor = (n: number) => RATING_COLORS[Math.min(5, Math.max(1, n)) - 1];

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Pill({ text, color = 'var(--accent)' }: { text: string; color?: string }) {
  return <span className="pill" style={{ color }}>{text}</span>;
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className="switch" onClick={() => onChange(!checked)} />;
}

export function ToggleRow({ title, detail, checked, onChange, extra }: {
  title: string; detail?: string; checked: boolean; onChange: (v: boolean) => void; extra?: ReactNode;
}) {
  return (
    <div className="toggle-row">
      <div className="text">
        <div className="row"><h2 style={{ fontSize: 16 }}>{title}</h2>{extra}</div>
        {detail && <p className="caption">{detail}</p>}
      </div>
      <Switch checked={checked} onChange={onChange} label={title} />
    </div>
  );
}

/** 1–5 rating, used for intensity, recovery and the daily check-in. */
export function RatingPicker({ title, labels, value, onChange, invert = false }: {
  title: string; labels: string[]; value: number | null; onChange: (v: number) => void; invert?: boolean;
}) {
  const color = (n: number) => ratingColor(invert ? 6 - n : n);
  return (
    <div className="stack" role="group" aria-label={title}>
      <div className="row">
        <h2>{title}</h2>
        <span className="spacer" />
        {value != null && <span className="caption" style={{ color: color(value) }}>{labels[value - 1]}</span>}
      </div>
      <div className="rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" aria-label={`${title} ${n}, ${labels[n - 1]}`} aria-pressed={value === n}
            style={value === n ? { background: color(n), color: 'var(--bg)' } : undefined} onClick={() => onChange(n)}>{n}</button>
        ))}
      </div>
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange, label }: {
  options: [T, string][]; value: T; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(([v, text]) => <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{text}</button>)}
    </div>
  );
}

export function Sheet({ title, onClose, children, footer, leading }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; leading?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    // Modal: the app behind is inert (no focus, hidden from screen readers) and does not scroll.
    const root = document.getElementById('root');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    root?.setAttribute('inert', '');
    const opener = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      root?.removeAttribute('inert');
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);
  return createPortal(
    <div className="sheet-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <header>
          {leading ?? <span style={{ minWidth: 56 }} />}
          <h2>{title}</h2>
          <button className="link" style={{ textAlign: 'right' }} onClick={onClose}>Close</button>
        </header>
        <div className="body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export function Empty({ title, detail }: { title: string; detail: string }) {
  return <div className="empty"><h2 style={{ color: 'var(--text)' }}>{title}</h2><p>{detail}</p></div>;
}

const ICONS: Record<string, string> = {
  today: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 4v5l3 2',
  history: 'M4 6h16M4 12h16M4 18h10',
  notes: 'M5 5h14v10H9l-4 4V5Z',
  you: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0',
};

export function Icon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}
